import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeftRight } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useTransferPatient } from "@/hooks";
import { ROUTES } from "@/router";
import { useMultiStepFormStore } from "@/stores";
import type { TransferFormType } from "@/types";
import { transferSchema } from "@/types";
import {
  Banner,
  ComboboxWithLabels,
  FormField,
  NotQualifyModal,
  Spinner,
} from "@/ui";
import FormButtons from "./FormButtons";

export const TransferForm = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { setMultiStepFormData, patientIdToTransfer, clearMultiStepFormData } =
    useMultiStepFormStore();
  const { usePayors, useSendToCompliantRX } = useTransferPatient();
  const { data: payorsOptions, isLoading: isPayorsLoading } = usePayors(
    patientIdToTransfer,
    !!patientIdToTransfer,
  );

  const { mutate: transferPatient, isPending: isTransferring } =
    useSendToCompliantRX();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TransferFormType>({
    resolver: zodResolver(transferSchema),
  });

  const onSubmit: SubmitHandler<TransferFormType> = (
    data: TransferFormType,
  ) => {
    setMultiStepFormData({ transferInformationFormData: data });
    transferPatient(
      {
        transferId: patientIdToTransfer,
        payorId: data.payorId,
      },
      {
        onSuccess: (response) => {
          void navigate(ROUTES.endScreenTransferred, {
            state: { dmeProvider: response.dmeProvider },
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!isPayorsLoading && payorsOptions?.length === 0) {
      setIsModalOpen(true);
    }
  }, [isPayorsLoading, payorsOptions]);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="text-primary-600" size={16} />
          <p className="font-extrabold">Transfer information</p>
        </div>
        <p className="flex text-sm text-neutral-500">All fields are required</p>
      </div>
      <Banner variant="warning">
        <p>
          Due to the limitations of the patient&rsquo;s insurance, Quest Health
          is unable to proceed. We will facilitate a transfer to another DME
          provider.
        </p>
      </Banner>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <>
          {!isPayorsLoading ? (
            <FormField
              label="Please choose insurance provider"
              error={errors.payorId?.message}
            >
              <Controller
                control={control}
                name="payorId"
                render={({ field, fieldState }) => (
                  <ComboboxWithLabels
                    options={payorsOptions ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    hasError={!!fieldState.error?.message}
                    placeholder="Select or search by insurance name"
                  />
                )}
              />
            </FormField>
          ) : (
            <Spinner />
          )}
        </>

        <FormButtons
          isSubmitting={isTransferring}
          onSubmitOverride={
            payorsOptions?.length === 0
              ? () => {
                  setIsModalOpen(true);
                }
              : undefined
          }
        />
      </form>
      <NotQualifyModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleEndIntake={() => clearMultiStepFormData()}
        title={"No payors available"}
        description={
          'There are no payors available for the selected state. Please inform the patient to call their insurance company and ask for an "In-Network Provider"'
        }
      />
    </div>
  );
};
